import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { useLiveAction } from '../lib/data.ts';
import { usePlaces } from '../lib/places.tsx';
import { elapsed, money } from '../lib/format.ts';
import type { FloorPlan, FloorTable, OrderView } from '../lib/types.ts';
import { useSession } from '../lib/session.tsx';
import { useNow, useUi } from '../lib/ui-state.tsx';
import type { IconName } from '../components/Icon.tsx';

export function tableStatus(tbl: FloorTable): { tone: Tone; icon: IconName; label: string; pulse?: boolean } {
  const o = tbl.order;
  if (!o) return { tone: 'neutral', icon: 'circle', label: t('table.free') };
  if (o.ready_tickets > 0) return { tone: 'ok', icon: 'bell', label: t('table.ready'), pulse: true };
  if (o.unsent_items > 0) return { tone: 'warn', icon: 'send', label: t('table.not_sent') };
  return { tone: 'info', icon: 'users', label: t('table.seated') };
}

export function Floor() {
  const navigate = useNavigate();
  const { fail } = useUi();
  const now = useNow();
  const { here, multi } = usePlaces();
  const { data, live } = useLiveAction<FloorPlan>('restaurant.floor_plan', multi && here ? { location: here.id } : {}, ['restaurant', 'sales']);
  const { can } = useSession();
  // Next booking per table, when reservations are in use.
  const { data: upcoming } = useLiveAction<Upcoming[]>('reservations.upcoming', {}, can('reservations.view') ? ['reservation'] : []);
  const bookingOf = (tableId: string) => (can('reservations.view') ? upcoming?.find((u) => u.table === tableId) : undefined);
  const [floorId, setFloorId] = useState<string | null>(null);
  const [opening, setOpening] = useState<FloorTable | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'free'>('all');

  // A device that moves to another location starts on that location's first floor.
  const floor = (floorId && data?.floors.some((f) => f.id === floorId) ? floorId : null) ?? data?.floors[0]?.id ?? null;
  const tables = useMemo(
    () =>
      (data?.tables ?? [])
        .filter((x) => x.floor === floor)
        .filter((x) => (filter === 'all' ? true : filter === 'open' ? !!x.order : !x.order)),
    [data, floor, filter],
  );
  const counts = useMemo(() => {
    const all = data?.tables ?? [];
    return {
      open: all.filter((x) => x.order).length,
      ready: all.filter((x) => (x.order?.ready_tickets ?? 0) > 0).length,
      free: all.filter((x) => !x.order).length,
    };
  }, [data]);
  const cols = Math.max(12, ...tables.map((x) => x.x + x.w));

  const takeaway = async () => {
    try {
      const r = await action<OrderView>('restaurant.open_takeaway', { location: here?.id ?? null });
      navigate(`/pos/order/${r.order.id}`);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Shell live={live}>
      <div className="page floor-page">
        <div className="page-head">
          <div>
            <h1>{t('floor.title')}</h1>
            <p className="muted">
              {data ? t('floor.summary', { open: counts.open, free: counts.free }) : <Skeleton w={180} />}
              {counts.ready > 0 ? (
                <>
                  {' · '}
                  <strong className="text-ok">{tn('floor.ready_count', counts.ready)}</strong>
                </>
              ) : null}
            </p>
          </div>
          <div className="page-actions">
            <Button icon="bag" onClick={takeaway}>
              {t('floor.takeaway')}
            </Button>
          </div>
        </div>

        <div className="toolbar">
          <div className="segmented" role="tablist" aria-label={t('floor.floors')}>
            {(data?.floors ?? []).map((f) => (
              <button
                key={f.id}
                role="tab"
                aria-selected={f.id === floor}
                className={f.id === floor ? 'active' : ''}
                onClick={() => setFloorId(f.id)}
              >
                {f.name}
              </button>
            ))}
          </div>
          <div className="segmented subtle" role="tablist" aria-label={t('floor.filter')}>
            {(['all', 'open', 'free'] as const).map((k) => (
              <button key={k} role="tab" aria-selected={filter === k} className={filter === k ? 'active' : ''} onClick={() => setFilter(k)}>
                {t(`floor.filter_${k}`)}
              </button>
            ))}
          </div>
        </div>

        {!data ? (
          <div className="floor-grid list">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} h={128} r="var(--radius-lg)" />
            ))}
          </div>
        ) : tables.length === 0 ? (
          <EmptyState icon="table" title={t('floor.empty')} body={t('floor.empty_body')} />
        ) : (
          <div className="floor-grid" style={{ ['--cols' as string]: cols }}>
            {tables.map((tbl) => {
              const s = tableStatus(tbl);
              const o = tbl.order;
              const next = bookingOf(tbl.id);
              const booking = next ? (
                <span className={`table-booking${next.late ? ' late' : ''}`}>
                  <Icon name="calendar" size={14} />
                  {t('floor.booked', { time: next.time, n: next.guests })}
                </span>
              ) : null;
              return (
                <button
                  key={tbl.id}
                  className={`table-card tone-${s.tone} shape-${tbl.shape}`}
                  style={{
                    ['--x' as string]: tbl.x + 1,
                    ['--y' as string]: tbl.y + 1,
                    ['--w' as string]: tbl.w,
                    ['--h' as string]: tbl.h,
                  }}
                  onClick={() => (o ? navigate(`/pos/order/${o.id}`) : setOpening(tbl))}
                  aria-label={[t('table.name', { name: tbl.name }), s.label, next ? t('floor.booked', { time: next.time, n: next.guests }) : ''].filter(Boolean).join(', ')}
                >
                  <span className="table-top">
                    <span className="table-name">{tbl.name}</span>
                    <StatusBadge {...s} />
                  </span>
                  {o ? (
                    <span className="table-info">
                      <span className="table-total">{money(o.total)}</span>
                      <span className="table-meta">
                        <span>
                          <Icon name="users" size={14} /> {o.guests || '–'}
                        </span>
                        <span>
                          <Icon name="clock" size={14} /> {elapsed(o.opened_at, now)}
                        </span>
                      </span>
                      {booking}
                    </span>
                  ) : (
                    <span className="table-info">
                      <span className="table-seats">{tn('table.seats', tbl.seats)}</span>
                      {booking}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {data && data.other_orders.length > 0 ? (
          <section className="other-orders">
            <h2>{t('floor.other_orders')}</h2>
            <div className="chips">
              {data.other_orders.map((o) => (
                <button key={o.id} className="order-chip" onClick={() => navigate(`/pos/order/${o.id}`)}>
                  <Icon name="bag" size={16} />
                  <span>
                    {o.name} · #{o.number}
                  </span>
                  <strong>{money(o.total)}</strong>
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </div>
      {opening ? <OpenTable table={opening} booking={bookingOf(opening.id)} onClose={() => setOpening(null)} /> : null}
    </Shell>
  );
}

interface Upcoming {
  id: string;
  table: string;
  guests: number;
  time: string;
  name: string;
  allergies: string | null;
  vip: boolean;
  late: boolean;
}

function OpenTable({ table, booking, onClose }: { table: FloorTable; booking?: Upcoming; onClose: () => void }) {
  const navigate = useNavigate();
  const { fail } = useUi();
  const [guests, setGuests] = useState(Math.min(2, table.seats));
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const r = await action<OrderView>('restaurant.open_table', { table: table.id, guests });
      navigate(`/pos/order/${r.order.id}`);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  const seatBooking = async () => {
    setBusy(true);
    try {
      const r = await action<{ order: string }>('reservations.seat', { reservation: booking!.id });
      navigate(`/pos/order/${r.order}`);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  const quick = Array.from({ length: Math.max(8, table.seats) }, (_, i) => i + 1).slice(0, 12);
  return (
    <Dialog
      title={t('open_table.title', { name: table.name })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={open} busy={busy} data-autofocus>
            {t('open_table.submit')}
          </Button>
        </>
      }
    >
      {booking ? (
        <div className={`booking-callout${booking.late ? ' late' : ''}`}>
          <span>
            <Icon name="calendar" size={16} />
            <span>{t('open_table.booked', { time: booking.time, name: booking.name, n: booking.guests })}</span>
          </span>
          <Button size="sm" variant="primary" busy={busy} onClick={() => void seatBooking()}>
            {t('open_table.seat_booking')}
          </Button>
        </div>
      ) : null}
      <p className="muted">{booking ? t('open_table.walk_in') : t('open_table.guests')}</p>
      <div className="guest-grid" role="radiogroup" aria-label={t('open_table.guests')}>
        {quick.map((n) => (
          <button key={n} role="radio" aria-checked={guests === n} className={`guest-btn${guests === n ? ' active' : ''}`} onClick={() => setGuests(n)}>
            {n}
          </button>
        ))}
      </div>
    </Dialog>
  );
}
